import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import TransactionTicketGroup from './TransactionTicketGroup';
import './TransactionsPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.solteros.it/speeddate/api';

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' / ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtMoney = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
};

const shortOrder = (orderId) => {
  if (!orderId) return '—';
  const digits = (orderId.match(/\d+/g) || []).join('');
  return digits.slice(-4) || orderId.slice(-6);
};

const parseRaw = (rawStr) => {
  if (!rawStr) return {};
  try {
    const raw = typeof rawStr === 'string' ? JSON.parse(rawStr) : rawStr;
    const ps = raw.payment_source?.paypal || {};
    const ship = raw.purchase_units?.[0]?.shipping || {};
    const addr = ship.address || {};
    const payer = raw.payer || {};
    const email = ps.email_address || payer.email_address || '';
    const name = [ps.name?.given_name || payer.name?.given_name, ps.name?.surname || payer.name?.surname]
      .filter(Boolean).join(' ') || ship.name?.full_name || '';
    const address = addr.address_line_1 || '';
    const city = addr.admin_area_2 || '';
    const province = addr.admin_area_1 || '';
    const cap = addr.postal_code || '';
    return { email, name, address, cityCap: [city, province, cap].filter(Boolean).join(' ') };
  } catch (e) {
    return {};
  }
};

const toDateInputValue = (date) => {
  return date.toISOString().split('T')[0];
};

export default function TransactionsPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [opened, setOpened] = useState(null);

  // ── Filter state ──
  const [search, setSearch] = useState('');
  const now = new Date();
  const defaultFrom = new Date(now);
  defaultFrom.setDate(defaultFrom.getDate() - 30);
  const [dateFrom, setDateFrom] = useState(toDateInputValue(defaultFrom));
  const [dateTo, setDateTo] = useState(toDateInputValue(now));

  // ── Debounce timer for search ──
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // ── Fetch ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const res = await axios.get(`${API_URL}/transactions`, {
        params,
        headers: { 'x-admin-token': token }
      });
      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Quick date range presets ──
  const setLast7Days = () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 7);
    setDateFrom(toDateInputValue(from));
    setDateTo(toDateInputValue(to));
  };
  const setLast30Days = () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    setDateFrom(toDateInputValue(from));
    setDateTo(toDateInputValue(to));
  };
  const setLast90Days = () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 90);
    setDateFrom(toDateInputValue(from));
    setDateTo(toDateInputValue(to));
  };
  const setThisYear = () => {
    const to = new Date();
    const from = new Date(to.getFullYear(), 0, 1);
    setDateFrom(toDateInputValue(from));
    setDateTo(toDateInputValue(to));
  };

  // Build set of captureIds that already have full refunds
  const refundedCaptureIds = new Set(
    data.filter(t => t.type === 'refund' && t.captureId).map(t => t.captureId)
  );

  const handleRefund = async (tx) => {
    if (!tx.captureId) {
      window.Swal.fire('Errore', 'Capture ID non trovato per questa transazione', 'error');
      return;
    }

    // SOLA-140: il costo del servizio PayPal non è rimborsabile. Il massimo
    // rimborsabile è il totale incassato meno la service fee (0 per NEXI).
    const isPayPal = tx.paymentMethod === 'PayPal';
    const totalAmount = Math.round((parseFloat(tx.amount) || 0) * 100) / 100;
    const serviceFee = isPayPal ? (parseFloat(tx.serviceFee) || 0) : 0;
    const maxRefundable = Math.max(0, Math.round((totalAmount - serviceFee) * 100) / 100);
    const fullRefundable = Math.max(maxRefundable, totalAmount);
    if (fullRefundable <= 0) {
      window.Swal.fire('Errore', 'Importo rimborsabile pari a 0', 'error');
      return;
    }
    const feeNote = serviceFee > 0
      ? `<div class="alert alert-info py-2 px-3 mb-3 small">Costo del servizio PayPal <b>${fmtMoney(serviceFee)}</b>: non rimborsabile, salvo spunta qui sotto.</div>
         <div class="form-check mb-3">
           <input class="form-check-input" type="checkbox" id="refund-service-fee" />
           <label class="form-check-label small" for="refund-service-fee">Rimborsa <b>anche</b> il costo del servizio (€${serviceFee.toFixed(2)})</label>
         </div>`
      : `<div class="text-muted small mb-3"><i class="bi bi-info-circle me-1"></i>Nessun costo del servizio da rimborsare su questa transazione (NEXI oppure transazione precedente all'introduzione della fee).</div>`;

    const { value: formResult } = await window.Swal.fire({
      title: 'Rimborso Transazione',
      html: `
        <p class="text-muted mb-3">Ordine: <b>${shortOrder(tx.referenceOrderId || tx.orderId)}</b></p>
        <p class="text-muted mb-3">Importo originale: <b>${fmtMoney(tx.amount)}</b></p>
        <p class="text-muted mb-3">Metodo: <b>${tx.paymentMethod || '—'}</b></p>
        ${feeNote}
        <div class="mb-2">
          <label class="form-label fw-bold">Importo rimborso</label>
          <input id="refund-amount" class="form-control" type="number" step="0.01" min="0.01" max="${maxRefundable}" value="${maxRefundable}" />
        </div>
        <div>
          <label class="form-label fw-bold">Motivo (opzionale)</label>
          <input id="refund-reason" class="form-control" type="text" placeholder="es. Annullato per maltempo" />
        </div>
      `,
      focusConfirm: false,
      didOpen: () => {
        const checkbox = document.getElementById('refund-service-fee');
        const input = document.getElementById('refund-amount');
        if (checkbox && input) {
          checkbox.addEventListener('change', () => {
            const max = checkbox.checked ? fullRefundable : maxRefundable;
            input.max = max;
            input.value = max;
          });
        }
      },
      showCancelButton: true,
      confirmButtonText: 'Rimborsa',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#d33',
      preConfirm: () => {
        return {
          amount: document.getElementById('refund-amount').value,
          reason: document.getElementById('refund-reason').value,
          refundServiceFee: !!document.getElementById('refund-service-fee')?.checked
        };
      }
    });

    if (!formResult) return;

    try {
      const token = localStorage.getItem('adminToken');
      const endpoint = isPayPal ? `${API_URL}/payments/paypal/refund` : `${API_URL}/payments/igfs/refund`;
      const body = isPayPal ? {
        captureId: tx.captureId,
        amount: formResult.amount,
        reason: formResult.reason,
        refundServiceFee: formResult.refundServiceFee
      } : {
        orderId: tx.orderId,
        amount: formResult.amount,
        reason: formResult.reason,
        refundServiceFee: formResult.refundServiceFee
      };

      const res = await axios.post(endpoint, body, { headers: { 'x-admin-token': token } });

      if (res.data.success) {
        window.Swal.fire({
          title: 'Rimborso Completato!',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        setData(prev => [res.data.refund, ...prev]);
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Errore durante il rimborso';
      window.Swal.fire('Errore Rimborso', msg, 'error');
    }
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fw-bold mb-0">
          <i className="bi bi-receipt"></i> Transazioni
        </h4>
        {!loading && <span className="badge bg-primary rounded-pill">{data.length}</span>}
      </div>

      {/* ── Filtri ── */}
      <div className="card border-0 shadow-sm glass mb-3">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            {/* Search */}
            <div className="col-md-4">
              <label className="form-label small fw-bold text-muted mb-1">
                <i className="bi bi-search me-1"></i>Cerca
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="ordine, nome, email, evento, biglietto..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            {/* Date from */}
            <div className="col-md-2">
              <label className="form-label small fw-bold text-muted mb-1">Da</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
              />
            </div>

            {/* Date to */}
            <div className="col-md-2">
              <label className="form-label small fw-bold text-muted mb-1">A</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
              />
            </div>

            {/* Quick presets */}
            <div className="col-md-4">
              <label className="form-label small fw-bold text-muted mb-1">&nbsp;</label>
              <div className="d-flex gap-1">
                <button className="btn btn-sm btn-outline-secondary" onClick={setLast7Days}>7gg</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={setLast30Days}>30gg</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={setLast90Days}>90gg</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={setThisYear}>Anno</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm glass">
        {loading ? (
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Caricamento...</span>
            </div>
          </div>
        ) : error ? (
          <div className="card-body text-center py-5">
            <i className="bi bi-exclamation-triangle text-danger fs-1"></i>
            <p className="text-danger mt-3">Errore nel caricamento delle transazioni</p>
            <p className="text-muted small">{error}</p>
          </div>
        ) : (
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 transactions-table">
                <thead className="table-light">
                  <tr>
                    <th colSpan={11} className="group-header text-center small text-uppercase fw-bold text-muted py-2 ps-4 bg-warning-subtle border">Dati Transazione</th>
                    <th colSpan={4} className="group-header text-center small text-uppercase fw-bold text-muted py-2 bg-info-subtle border">Dati Evento</th>
                    <th colSpan={3} className="group-header text-center small text-uppercase fw-bold text-muted py-2 pe-4 bg-success-subtle border">Dati Biglietti</th>
                  </tr>
                  <tr>
                    <th className="small text-uppercase fw-bold text-muted py-3 ps-4 bg-warning-subtle">Data/Ora</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 bg-warning-subtle">Tipo</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 text-end bg-warning-subtle">Valore</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 text-end bg-warning-subtle">Tariffe</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 text-end bg-warning-subtle">Netto</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 bg-warning-subtle">Metodo Pagamento</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 bg-warning-subtle">Ordine n°</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 bg-warning-subtle">Acquirente</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 bg-warning-subtle">Indirizzo</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 bg-warning-subtle">Città / CAP</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 pe-4 bg-warning-subtle">Azioni</th>

                    <th className="small text-uppercase fw-bold text-muted py-3 bg-info-subtle">Evento</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 bg-info-subtle">Data Evento</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 bg-info-subtle">Città Evento</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 bg-info-subtle">Utente</th>

                    <th className="small text-uppercase fw-bold text-muted py-3 bg-success-subtle" style={{width:'30px'}}>Qty</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 bg-success-subtle">Biglietto</th>
                    <th className="small text-uppercase fw-bold text-muted py-3 pe-4 bg-success-subtle">Partecipante/i</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={18} className="text-center text-muted py-5">
                        <i className="bi bi-inbox d-block fs-1 mb-2"></i>
                        Nessuna transazione trovata
                      </td>
                    </tr>
                  ) : data.map(tx => {
                    const rawData = parseRaw(tx.raw);
                    const address = rawData.address || '—';
                    const cityCap = rawData.cityCap || '—';

                    const acquirenteName = tx.payerName || rawData.name || '—';
                    const acquirenteEmail = rawData.email || tx.payerEmail || '';

                    const utenteName = [tx.user?.firstName, tx.user?.lastName].filter(Boolean).join(' ');
                    const utenteEmail = tx.user?.email || '';

                    // Group items by ticket type
                    const items = tx.items && tx.items.length > 0 ? tx.items : null;
                    const groups = items ? (() => {
                      const map = new Map();
                      items.forEach(it => {
                        const key = it.ticketId || it.ticketName || 'other';
                        if (!map.has(key)) map.set(key, []);
                        map.get(key).push(it);
                      });
                      return Array.from(map.entries()).map(([k, v], idx) => ({ key: k, idx, items: v }));
                    })() : null;
                    // Fallback: single pseudo-group for historical transactions without items
                    const displayGroups = groups && groups.length > 0 ? groups : [{ idx: 0, items: [] }];
                    // Count total rows across all groups
                    const itemCount = groups ? items.length : 1;

                    return (
                      <React.Fragment key={tx.id || tx.orderId}>
                        {displayGroups.map((group, gi) => (
                          <TransactionTicketGroup
                            key={group.key || gi}
                            group={group}
                            tx={tx}
                            opened={opened}
                            setOpened={setOpened}
                            handleRefund={handleRefund}
                            refundedCaptureIds={refundedCaptureIds}
                            address={address}
                            cityCap={cityCap}
                            acquirenteName={acquirenteName}
                            acquirenteEmail={acquirenteEmail}
                            utenteName={utenteName}
                            utenteEmail={utenteEmail}
                            fmtDateTime={fmtDateTime}
                            fmtDate={fmtDate}
                            fmtMoney={fmtMoney}
                            shortOrder={shortOrder}
                            isFirst={gi === 0}
                            itemCount={itemCount}
                          />
                        ))}
                        {opened === tx.id && (
                          <tr>
                            <td colSpan={18} className="p-0" style={{ background: '#f8f9fa' }}>
                              <div className="p-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <h6 className="mb-0 fw-bold">
                                    <i className="bi bi-file-earmark-text me-2"></i>Dati grezzi della transazione
                                  </h6>
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setOpened(null)}
                                  >
                                    <i className="bi bi-x-lg"></i> Chiudi
                                  </button>
                                </div>
                                {tx.items && tx.items.length > 0 && (
                                  <div className="card border-0 shadow-sm mb-3">
                                    <div className="card-body p-3">
                                      <h6 className="fw-bold mb-3">
                                        <i className="bi bi-ticket-perforated me-2"></i>Biglietti & Partecipanti
                                      </h6>
                                      <div className="table-responsive">
                                        <table className="table table-sm align-middle mb-0">
                                          <thead className="table-light">
                                            <tr>
                                              <th className="small text-uppercase fw-bold text-muted">Biglietto</th>
                                              <th className="small text-uppercase fw-bold text-muted text-end">Prezzo</th>
                                              <th className="small text-uppercase fw-bold text-muted">Tipo</th>
                                              <th className="small text-uppercase fw-bold text-muted">Partecipante</th>
                                              <th className="small text-uppercase fw-bold text-muted">Email</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {tx.items.map((it, i) => (
                                              <tr key={i}>
                                                <td className="fw-semibold">
                                                  {it.ticketName || '—'}
                                                  {it.optional ? <span className="badge bg-warning-subtle text-dark ms-1" style={{fontSize:'10px'}}>Opz</span> : null}
                                                  {it.participantId && it.participantId === tx.ParticipantId
                                                    ? <span className="badge bg-primary-subtle text-primary ms-2" style={{fontSize:'10px'}}>Pagante</span>
                                                    : it.participantId
                                                      ? <span className="badge bg-info-subtle text-info-emphasis ms-2" style={{fontSize:'10px'}}>Amica/o</span>
                                                      : null}
                                                </td>
                                                <td className="text-end fw-bold">€{parseFloat(it.price || 0).toFixed(2)}</td>
                                                <td>
                                                  {it.optional ? (
                                                    <span className="badge bg-warning-subtle text-dark">Opzionale</span>
                                                  ) : (
                                                    <span className="badge bg-secondary-subtle text-secondary">
                                                      {it.sex === 'male' ? 'Uomo' : it.sex === 'female' ? 'Donna' : '—'}
                                                    </span>
                                                  )}
                                                </td>
                                                <td className="fw-semibold">
                                                  {it.participantData
                                                    ? [it.participantData.firstName, it.participantData.lastName].filter(Boolean).join(' ') || '—'
                                                    : '—'}
                                                </td>
                                                <td className="text-muted small">{it.participantData?.email || it.participantData?.participantEmail || '—'}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                <pre
                                  style={{
                                    background: '#0f172a',
                                    color: '#e2e8f0',
                                    padding: '14px',
                                    borderRadius: '8px',
                                    maxHeight: '420px',
                                    overflow: 'auto',
                                    fontSize: '12px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {(() => {
                                    if (!tx.raw) return 'Nessun dato grezzo disponibile';
                                    try { return JSON.stringify(JSON.parse(tx.raw), null, 2); }
                                    catch (e) { return tx.raw; }
                                  })()}
                                </pre>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}